import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../api/client';
import { BOARD_MANUAL_PATH } from '../constants/routes';
import type { ManualDetailResponse } from '../constants/manual';
import { resolveStaffAvatarSrc } from '../constants/defaultAvatar';
import { BulletinAttachmentTypeIcon } from '../constants/bulletinAttachmentUi';
import AttachmentImagePreviewDialog from './AttachmentImagePreviewDialog';
import BulletinFormattedBody from './BulletinFormattedBody';
import { formatPostTime, isEditedSinceCreated } from '../utils/formatPostTime';
import { upsertReaderReadAtOptimistic } from '../utils/optimisticReaderMerge';
import { useAppSnackbar } from '../snackbar/useAppSnackbar';
import { useAuth } from '../auth/AuthContext';

function isImageContentType(ct: string): boolean {
  return ct.toLowerCase().startsWith('image/');
}

const ManualDetail: React.FC = () => {
  const { staffProfile } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useAppSnackbar();
  const manualId = id && /^\d+$/.test(id) ? Number(id) : NaN;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState<ManualDetailResponse | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<number, string>>({});
  const [previewImage, setPreviewImage] = useState<{ src: string; alt: string } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isInteger(manualId) || manualId < 1) {
      setError('マニュアル ID が不正です');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get<ManualDetailResponse>(`/api/manual/manuals/${manualId}`);
      setDetail(res.data);
      try {
        await apiClient.post(`/api/manual/manuals/${manualId}/read`);
        setDetail((prev) => {
          if (!prev || !staffProfile || prev.manual.last_edited_by.staff_user_id === staffProfile.id) {
            return prev;
          }
          return {
            ...prev,
            readers: upsertReaderReadAtOptimistic(
              prev.readers,
              staffProfile.id,
              new Date().toISOString(),
              staffProfile,
            ),
          };
        });
      } catch {
        /* 既読失敗は画面表示を優先 */
      }
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        setError('マニュアルが見つかりません');
      } else {
        setError('マニュアルの取得に失敗しました');
      }
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [manualId, staffProfile]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!detail?.manual.attachments.length) return;
    let cancelled = false;
    const run = async () => {
      const next: Record<number, string> = {};
      for (const a of detail.manual.attachments) {
        if (!isImageContentType(a.content_type)) continue;
        try {
          const u = await apiClient.get<{ download_url: string }>(
            `/api/manual/manuals/${manualId}/attachments/${a.id}/url`
          );
          if (!cancelled) next[a.id] = u.data.download_url;
        } catch {
          /* プレビュー省略 */
        }
      }
      if (!cancelled) setImageUrls(next);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [detail, manualId]);

  const handleDownload = async (attachmentId: number, filename: string) => {
    try {
      const u = await apiClient.get<{ download_url: string }>(
        `/api/manual/manuals/${manualId}/attachments/${attachmentId}/url`
      );
      const a = document.createElement('a');
      a.href = u.data.download_url;
      a.download = filename;
      a.rel = 'noopener';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setError('ダウンロード用 URL の取得に失敗しました');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/api/manual/manuals/${manualId}`);
      showSnackbar({ message: 'マニュアルを削除しました', severity: 'success' });
      navigate(BOARD_MANUAL_PATH);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data && typeof e.response.data === 'object'
          ? (e.response.data as { error?: string }).error
          : null;
      showSnackbar({ message: msg || 'マニュアルの削除に失敗しました', severity: 'error' });
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          maxWidth: 960,
          width: '100%',
          mx: 'auto',
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || '読み込みに失敗しました'}
        </Alert>
      </Box>
    );
  }

  const { manual, readers } = detail;

  return (
    <Box sx={{ maxWidth: 960, width: '100%', mx: 'auto', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {manual.title}
        </Typography>
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/manual/${manualId}/edit`)}>
          編集
        </Button>
        <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>
          削除
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {manual.last_edited_by.display_name}・{formatPostTime(manual.updated_at)}
        {isEditedSinceCreated(manual.created_at, manual.updated_at) ? '（編集済み）' : ''}
      </Typography>

      <Paper sx={{ p: 2, mb: 2, whiteSpace: 'pre-wrap' }}>
        <BulletinFormattedBody body={manual.body} />
      </Paper>

      {readers.length > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <AvatarGroup
            max={5}
            sx={{
              // 透過PNGの重なりで背面のアイコンが見えないよう白背景にする
              '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.875rem', bgcolor: '#fff' },
            }}
          >
            {readers.map((r) => (
              <Tooltip
                key={r.staff_user_id}
                placement="top"
                describeChild
                title={
                  <Box>
                    <Typography variant="body2" component="div" fontWeight={600}>
                      {r.display_name || '（名前なし）'}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ opacity: 0.9, display: 'block', mt: 0.25 }}>
                      閲覧:{' '}
                      {new Date(r.read_at).toLocaleString('ja-JP', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                }
              >
                <Avatar
                  src={resolveStaffAvatarSrc(r.reader_avatar_url)}
                  alt={r.display_name ? `${r.display_name}の閲覧` : '閲覧'}
                >
                  {(r.display_name || '?').charAt(0)}
                </Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        </Box>
      ) : null}

      {manual.attachments.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            添付ファイル
          </Typography>
          <List dense disablePadding>
            {manual.attachments.map((a) => (
              <ListItemButton
                key={a.id}
                onClick={() => void handleDownload(a.id, a.original_filename)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <BulletinAttachmentTypeIcon contentType={a.content_type} fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleDownload(a.id, a.original_filename);
                        }}
                        sx={{ textAlign: 'left' }}
                      >
                        {a.original_filename}
                      </Link>
                      <DownloadIcon fontSize="small" color="action" />
                    </Box>
                  }
                  secondary={`${(a.byte_size / 1024).toFixed(1)} KB`}
                />
              </ListItemButton>
            ))}
          </List>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            {manual.attachments.map((a) =>
              imageUrls[a.id] ? (
                <Box
                  key={`img-${a.id}`}
                  component="button"
                  type="button"
                  onClick={() => setPreviewImage({ src: imageUrls[a.id], alt: a.original_filename })}
                  aria-label={`${a.original_filename}を拡大表示`}
                  sx={{
                    p: 0,
                    m: 0,
                    border: 1,
                    borderColor: 'divider',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'block',
                    borderRadius: 1,
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineOffset: 2,
                      outlineColor: 'primary.main',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={imageUrls[a.id]}
                    alt={a.original_filename}
                    sx={{
                      maxWidth: 280,
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 1,
                      display: 'block',
                      pointerEvents: 'none',
                    }}
                  />
                </Box>
              ) : null
            )}
          </Box>
        </>
      )}

      <AttachmentImagePreviewDialog
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        src={previewImage?.src ?? null}
        alt={previewImage?.alt ?? ''}
      />

      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 2,
          },
        }}
      >
        <DialogTitle sx={{ pt: 3, px: 3, pb: 1, typography: 'h6' }}>マニュアルを削除しますか？</DialogTitle>
        <DialogContent sx={{ px: 3, pt: 1, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, flexWrap: 'wrap' }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting} sx={{ minWidth: 100 }}>
            キャンセル
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleDelete()} disabled={deleting} sx={{ minWidth: 100 }}>
            削除する
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualDetail;
